import * as React from "react";
import { Box, Typography, Button, AlertColor } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./OralHealthContent.scss";
import { infoIcon, teethIcon } from "../../../utils/common/global";
import CustomAlert from "../../../components/custom-alert/CustomAlert";
import { saveAssessmentResult } from "../../../features/user/asyncActions";
import { useAppSelector } from "../../../app/hooks";
import { getCurrentMember } from "../../../features/user/userSlice";
import { getUserData, loadUserData } from "../../../features/auth/authSlice";
import { store } from "../../../app/store";
import { useTranslation } from "react-i18next";
import {
  getGlobalLanguage,
  getSelectedAnswers,
  updateSelectedAnswers,
} from "../../../features/global/globalSlice";
import ConfirmationModal from "../../../components/confirmation-modal/ConfirmationModal";
import { useWidthSize } from "../../../utils/hooks/resizeHook";
import CustomSupportText from "../../../components/custom-support-text/CustomSupportText";
import AudioSupport from "../../../components/audio-support/AudioSupport";
import CustomHint from "../../../components/custom-hint/CustomHint";

interface OralHealthContentProps {
  allPreviousSelectedChoices: Array<any>;
  questionsWithChoices?: Array<any>;
  saveAssessmentHandler?: (val: any) => void;
  selectedQuest: any;
  setSelectedQuest: (val: any) => void;
}

const OralHealthContent = ({
  questionsWithChoices,
  selectedQuest,
  setSelectedQuest,
}: OralHealthContentProps) => {
  const { t } = useTranslation();
  const customWidth = useWidthSize();
  const navigate = useNavigate();
  const userData = useAppSelector(getUserData);
  const memberData = useAppSelector(getCurrentMember);
  const [showBubbleInfo, setShowBubbleInfo] = React.useState(false);
  const [showModalInfo, setShowModalInfo] = React.useState(false);
  const [selectedChoices, setSelectedChoices] = React.useState<any>([]);
  const [showMessage, setShowMessage] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string>("");
  const [messageType, setMessageType] = React.useState<AlertColor>("error");
  const language = useAppSelector(getGlobalLanguage);
  const selectedAnswers = useAppSelector(getSelectedAnswers);

  const selectedChoicesHandler = (id: string, selectedChoice: any) => {
    const foundedChoice = selectedChoices.find(
      (choice: any) => choice.questionID === id
    );
    if (foundedChoice) {
      const newSelectedChoices = selectedChoices.filter(
        (choice: any) => choice.questionID !== id
      );
      if (foundedChoice.choiceID !== selectedChoice.choiceID) {
        setSelectedChoices([
          ...newSelectedChoices,
          { questionID: id, ...selectedChoice },
        ]);
      } else {
        setSelectedChoices([...newSelectedChoices]);
      }
    } else {
      setSelectedChoices([
        ...selectedChoices,
        { questionID: id, ...selectedChoice },
      ]);
    }
  };

  const submitHandler = async (ev: any) => {
    ev.preventDefault();
    if (questionsWithChoices?.length === selectedChoices.length) {
      const newSelectedChoices = selectedChoices
        .map((choice: any) => choice.choiceID)
        .sort((a: any, b: any) => a - b);
      const allSelectedChoices = [
        ...selectedAnswers.diet,
        ...selectedAnswers.feeding,
        ...selectedAnswers.brushing,
        ...selectedAnswers.fluoride,
        ...selectedAnswers.dentalHome,
        ...newSelectedChoices,
      ];
      store.dispatch(
        updateSelectedAnswers({
          ...selectedAnswers,
          oralHealthAnswers: selectedChoices,
          oralHealth: newSelectedChoices,
        })
      );
      await saveAssessmentResult({
        memberId: memberData.user_id,
        selectedChoices: allSelectedChoices,
      });
      await store.dispatch(loadUserData(userData?.group_id));
      navigate("/goal-settings/intro");
    } else {
      setShowMessage(true);
      setMessage(t("common.23"));
      setMessageType("error");
      await store.dispatch(loadUserData(userData?.group_id));
    }
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    selectedAnswers?.oralHealthAnswers &&
      setSelectedChoices(selectedAnswers.oralHealthAnswers);
  }, []);

  return (
    <Box className="oralHealthContainer">
      <ConfirmationModal
        title=""
        open={showModalInfo}
        closeHandler={() => {
          setShowModalInfo(false);
          setSelectedQuest(null);
        }}
        showCancelButton={false}
        showSubmitButton={false}
      >
        <Box className="bubbleText">
          <CustomSupportText questionId={selectedQuest?.questionID} />
        </Box>
      </ConfirmationModal>
      <CustomAlert
        type={messageType}
        open={showMessage}
        closeHandler={() => setShowMessage(false)}
        message={message}
      />
      <>
        <Box marginTop="2rem">
          <CustomHint
            text={t("dental-home.2")}
            audioText={t("dental-home.2")}
            bottom="-50px"
            left="-50px"
          />
        </Box>

        <Box className="content">
          <Box className="oralHealthContent">
            {questionsWithChoices?.map((quest) => (
              <Box className="questionBox" key={quest.questionID}>
                <AudioSupport
                  audioText={t(`questions.${quest.questionID}`)}
                  width="fit-content"
                />
                <Typography className="questionTitle">
                  {t(`questions.${quest.questionID}`)}
                </Typography>
                {selectedQuest?.questionID !== quest.questionID && (
                  <img
                    src={infoIcon}
                    alt=""
                    width={30}
                    height={30}
                    style={{
                      objectFit: "contain",
                      marginLeft: "0.5rem",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      if (customWidth > 1100) {
                        setShowBubbleInfo(true);
                      } else {
                        setShowModalInfo(true);
                      }
                      setSelectedQuest(quest);
                    }}
                  />
                )}
                <Box className="choicesBtnContainer">
                  {quest?.choices.map((choice: any) => (
                    <Button
                      key={choice.choiceID}
                      className={`choiceBtn ${
                        selectedChoices.find(
                          (item: any) =>
                            item.questionID === quest.questionID &&
                            item.choiceID === choice.choiceID
                        ) && "choiceBtn--selected"
                      }`}
                      onClick={() =>
                        selectedChoicesHandler(quest.questionID, choice)
                      }
                    >
                      {t(`choices.${choice.choiceID}`)}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
          <Box
            className="bubblesContainer"
            display={showBubbleInfo ? "block" : "none"}
          >
            {questionsWithChoices?.map((quest) => (
              <Box
                key={quest?.questionID}
                className="bubbleTextContainer"
                visibility={
                  selectedQuest?.questionID === quest?.questionID
                    ? "inherit"
                    : "hidden"
                }
              >
                <img
                  src={infoIcon}
                  alt=""
                  width={30}
                  height={30}
                  style={{
                    objectFit: "contain",
                    marginLeft: "0.5rem",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowBubbleInfo(false);
                    setSelectedQuest(null);
                  }}
                />
                <Box className="bubbleText">
                  <CustomSupportText questionId={quest?.questionID} />
                </Box>
                <img src={teethIcon} alt="" />
              </Box>
            ))}
          </Box>
        </Box>
        <Button
          className="btn"
          variant="contained"
          onClick={(ev) => submitHandler(ev)}
        >
          {t("common.14")}
        </Button>
      </>
    </Box>
  );
};

export default OralHealthContent;
